
import { Button } from "@/components/ui/button";
import { Check } from "lucide-react";

interface PortfolioTemplatesProps {
  currentTemplate: string;
  onSelectTemplate: (template: string) => void;
}

interface TemplateOption {
  id: string;
  name: string;
  description: string;
  image: string;
  isPremium?: boolean;
}

export const PortfolioTemplates = ({
  currentTemplate,
  onSelectTemplate
}: PortfolioTemplatesProps) => {
  const templates: TemplateOption[] = [
    {
      id: "minimal",
      name: "Minimal",
      description: "Clean and simple design focusing on your work",
      image: "https://images.unsplash.com/photo-1531403009284-440f080d1e12?q=80&w=600&auto=format&fit=crop"
    },
    {
      id: "gallery",
      name: "Gallery",
      description: "Grid-based layout ideal for visual portfolios",
      image: "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?q=80&w=600&auto=format&fit=crop"
    },
    {
      id: "magazine",
      name: "Magazine",
      description: "Editorial-inspired layout with featured sections",
      image: "https://images.unsplash.com/photo-1552664730-d307ca884978?q=80&w=600&auto=format&fit=crop"
    },
    {
      id: "creative",
      name: "Creative",
      description: "Bold, expressive design for artistic portfolios",
      image: "https://images.unsplash.com/photo-1493421419110-74f4e85ba126?q=80&w=600&auto=format&fit=crop",
      isPremium: true
    },
    {
      id: "professional",
      name: "Professional",
      description: "Corporate-ready design for business portfolios",
      image: "https://images.unsplash.com/photo-1507679799987-c73779587ccf?q=80&w=600&auto=format&fit=crop"
    },
    {
      id: "resume",
      name: "Resume",
      description: "CV-style layout highlighting your skills and experience",
      image: "https://images.unsplash.com/photo-1586281380349-632531db7ed4?q=80&w=600&auto=format&fit=crop"
    }
  ];

  return (
    <div className="p-6">
      <h3 className="text-xl font-semibold mb-6">Choose a Template</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {templates.map((template) => (
          <div 
            key={template.id} 
            className={`
              rounded-lg overflow-hidden border hover:shadow-md transition-all duration-200
              ${currentTemplate === template.id ? 'ring-2 ring-indigo-500 border-indigo-500' : 'border-gray-200'}
              ${template.isPremium ? 'relative' : ''}
            `}
          >
            {template.isPremium && (
              <div className="absolute top-2 right-2 z-10 bg-amber-500 text-white text-xs px-2 py-1 rounded-full font-medium">
                Premium
              </div>
            )}
            
            <div className="aspect-[4/3] overflow-hidden bg-gray-100">
              <img 
                src={template.image} 
                alt={template.name} 
                className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
              />
            </div>
            
            <div className="p-4">
              <div className="flex items-center justify-between mb-1">
                <h4 className="font-medium">{template.name}</h4>
                {currentTemplate === template.id && (
                  <Check className="h-4 w-4 text-indigo-500" />
                )}
              </div>
              <p className="text-sm text-gray-500 mb-4">
                {template.description}
              </p>
              <Button 
                variant={currentTemplate === template.id ? "default" : "outline"}
                size="sm"
                className="w-full"
                onClick={() => onSelectTemplate(template.id)}
                disabled={template.isPremium}
              >
                {currentTemplate === template.id ? "Selected" : "Use Template"}
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
